import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserCountsProvider } from './contexts/UserCountsContext';
import { LowStockProvider } from './contexts/LowStockContext';
import { SettingsProvider } from './contexts/SettingsContext';
import PermissionGuard from './components/PermissionGuard';
import Login from './components/Login';
import WorkflowManagement from './components/WorkflowManagement';
import Users from './components/Users';
import UsersHub from './components/UsersHub';
import TemporaryUserPayments from './components/TemporaryUserPayments';
import RiderPayments from './components/role-pages/RiderPayments';
import OwnerPayments from './components/role-pages/OwnerPayments';
import DebtsPage from './components/role-pages/DebtsPage';
import MiscellaneousPayments from './components/MiscellaneousPayments';
import PersonnelPayments from './components/PersonnelPayments';
import OtherPayments from './components/OtherPayments';
import FinanceOverview from './components/FinanceOverview';
import Institutional from './components/Institutional';
import SuppliersManagement from './components/SuppliersManagement';
import SimpleInventoryManagement from './components/SimpleInventoryManagement';
import Sessions from './components/Sessions';
import SimplifiedSessionManagement from './components/SimplifiedSessionManagement';
import Payments from './components/Payments';
import EnhancedAttendanceManagement from './components/EnhancedAttendanceManagement';
import Reports from './components/Reports';
import AdminSettings from './components/AdminSettings';
import Layout from './components/Layout';
import './i18n';
import { socketService } from './services/WebSocketService';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';

function ProtectedRoute({ children }) {
  const { user, loading, connectionError, refreshUser } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Equestrian Club</h2>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <span className="text-2xl">📡</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
          <p className="text-gray-600 mb-6">
            We are unable to connect to the server. Please check your internet connection or try again.
          </p>
          <button
            onClick={() => refreshUser()}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow Personnel (Admin/SuperAdmin) to access web app
  if (user.role !== 'Admin' && user.role !== 'SuperAdmin' && user.role !== 3 && user.role !== 4) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-red-100 mb-4">
            <span className="text-2xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-6">
            This web application is only available for Personnel users (Admin/SuperAdmin).
            <br /><br />
            <strong>Riders, Coaches, and Owners</strong> can access the system through this web portal.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">Web Portal Access</h3>
            <p className="text-sm text-blue-700">
              Use your <strong>username</strong> and password to access the web portal.
              <br />
              Username: <code className="bg-blue-100 px-1 rounded">{user.username}</code>
            </p>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('token');
              window.location.href = '/login';
            }}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return children;
}

// Imports moved to the top
function AppRoutes() {
  const { user } = useAuth(); // Assuming useAuth is available here via AuthProvider context

  useEffect(() => {
    if (user) {
      const setupSocket = async () => {
        await socketService.connect();

        socketService.on('UserUpdated', (userId) => {
          console.log('Real-time update: User', userId);
          toast.success(`User data updated (ID: ${userId})`, {
            icon: '🔄'
          });
        });
      };

      setupSocket();

      return () => {
        socketService.off('UserUpdated');
        // socketService.stop(); // Optional: keep alive
      };
    }
  }, [user]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Reports />} />
        <Route path="workflow" element={<WorkflowManagement />} />
        <Route path="users" element={<UsersHub />} />
        {/* Legacy redirect routes — preserve old sidebar links */}
        <Route path="subscribers"  element={<Navigate to="/users?tab=subscribers"  replace />} />
        <Route path="personnel"    element={<Navigate to="/users?tab=personnel"    replace />} />
        <Route path="coaches"      element={<Navigate to="/users?tab=coaches"      replace />} />
        <Route path="riders"       element={<Navigate to="/users?tab=riders"       replace />} />
        <Route path="owners"       element={<Navigate to="/users?tab=owners"       replace />} />
        <Route path="temporary"    element={<Navigate to="/users?tab=temporary"    replace />} />
        <Route path="temporary/payments" element={<TemporaryUserPayments />} />
        <Route path="payments" element={<Payments />} />
        <Route path="payments/riders" element={<RiderPayments />} />
        <Route path="payments/owners" element={<OwnerPayments />} />
        <Route path="debts" element={<DebtsPage />} />
        <Route path="institutional/incomes" element={<MiscellaneousPayments />} />
        <Route path="institutional" element={<Navigate to="/institutional/finance" replace />} />
        <Route path="institutional/finance" element={<FinanceOverview />} />
        <Route path="institutional/personnel" element={<PersonnelPayments />} />
        <Route path="institutional/other" element={<OtherPayments />} />
        <Route path="suppliers" element={<Navigate to="/inventory" replace />} />
        <Route path="inventory" element={
          <PermissionGuard requiredPermission="Access Inventory Management" pageName="Inventaire">
            <SimpleInventoryManagement />
          </PermissionGuard>
        } />
        <Route path="sessions" element={
          <PermissionGuard requiredPermission="Access Sessions" pageName="Séances">
            <SimplifiedSessionManagement />
          </PermissionGuard>
        } />
        <Route path="sessions-old" element={
          <PermissionGuard requiredPermission="Access Sessions" pageName="Séances (Ancien)">
            <Sessions />
          </PermissionGuard>
        } />
        <Route path="attendance" element={
          <PermissionGuard requiredPermission="Access Attendance" pageName="Présence">
            <EnhancedAttendanceManagement />
          </PermissionGuard>
        } />
        <Route path="admin/settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <UserCountsProvider>
        <LowStockProvider>
          <Router>
            <div className="App">
              <AppRoutes />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
          </LowStockProvider>
        </UserCountsProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
